/**
 * Healthcare facility profile activity toggles (Activities & Programs section).
 * Defaults err liberal (ON) so first-load compliance is comprehensive; users turn off to narrow.
 */

import type {
  ProfileFieldResponse,
  ProfileSectionResponse,
} from "@services/facilityManagement";

export const ACTIVITIES_SECTION_KEY = "activities";

/** Mirrors unicomply-rules healthcare_activity_defaults field keys. */
export const HEALTHCARE_ACTIVITY_FIELD_KEYS = [
  "performs_clia_regulated_testing",
  "handles_controlled_substances",
  "bills_federal_health_programs",
  "receives_federal_financial_assistance",
  "is_information_blocking_actor",
  "maintains_part2_sud_records",
  "handles_hctp",
  "stores_or_administers_vaccines",
  "performs_births",
  "hipaa_covered_entity",
  "generates_regulated_medical_waste",
  "handles_medications_on_site",
] as const;

export const ACTIVITIES_SECTION_HELPER_TEXT =
  "Activities are on by default so your compliance calendar is comprehensive. Turn off any that do not apply to narrow deadlines.";

const ACTIVITY_KEY_SET = new Set<string>(HEALTHCARE_ACTIVITY_FIELD_KEYS);

/** Legacy toggles superseded by Activities & Programs (hidden in Hub). */
export const DEPRECATED_PROFILE_FIELD_KEYS: ReadonlySet<string> = new Set([
  "section_1557_review",
  "oig_exclusion_monthly",
  "emergency_preparedness_plan_review",
]);

const DEA_FIELD_KEYS: ReadonlySet<string> = new Set([
  "dea_registration_number",
  "dea_registration_expiry",
  "dea_registration_expiration",
  "dea_registration_schedules",
  "dea_registration_business_activity",
]);

const FEDERAL_PROGRAM_FIELD_KEYS: ReadonlySet<string> = new Set([
  "ccn",
  "medicare_certification_date",
  "medicare_cert_date",
  "medicare_revalidation_date",
  "medicaid_revalidation_date",
  "is_dmepos",
]);

const CLIA_GATED_FIELD_KEYS: ReadonlySet<string> = new Set([
  "lab_anchor_active",
]);

function profileBool(values: Record<string, unknown>, fieldKey: string): boolean {
  const raw = values[fieldKey];
  if (raw === null || raw === undefined) return true;
  if (typeof raw === "boolean") return raw;
  if (typeof raw === "object" && raw !== null && "value" in raw) {
    const v = (raw as { value: unknown }).value;
    if (typeof v === "boolean") return v;
  }
  return Boolean(raw);
}

/** Hide deprecated keys and gate operational fields by activity master toggles. */
export function isDeprecatedProfileField(fieldKey: string): boolean {
  return DEPRECATED_PROFILE_FIELD_KEYS.has(fieldKey);
}

export function fieldVisibleForActivityToggles(
  field: ProfileFieldResponse,
  fieldValues: Record<string, unknown>,
): boolean {
  if (isDeprecatedProfileField(field.field_key)) return false;
  if (DEA_FIELD_KEYS.has(field.field_key)) {
    return profileBool(fieldValues, "handles_controlled_substances");
  }
  if (CLIA_GATED_FIELD_KEYS.has(field.field_key)) {
    return profileBool(fieldValues, "performs_clia_regulated_testing");
  }
  if (FEDERAL_PROGRAM_FIELD_KEYS.has(field.field_key)) {
    return profileBool(fieldValues, "bills_federal_health_programs");
  }
  const k = field.field_key.toLowerCase();
  if (k.startsWith("clia_")) {
    return profileBool(fieldValues, "performs_clia_regulated_testing");
  }
  if (
    k.includes("medicare_revalidation") ||
    (k.includes("medicare") && k.includes("cert"))
  ) {
    return profileBool(fieldValues, "bills_federal_health_programs");
  }
  return true;
}

export function isActivityProgramToggleField(
  section: ProfileSectionResponse,
  field: ProfileFieldResponse,
): boolean {
  const renderer = field.hub_renderer_key || field.field_type;
  if (renderer !== "toggle") return false;
  if (section.section_key === ACTIVITIES_SECTION_KEY) return true;
  return ACTIVITY_KEY_SET.has(field.field_key);
}

export function activityToggleValueUnset(val: unknown): boolean {
  return val === null || val === undefined;
}

/** Apply liberal ON when API/schema left an activity toggle without a value. */
export function applyLiberalActivityToggleDefaults(
  sections: ProfileSectionResponse[],
  values: Record<string, unknown>,
): Record<string, unknown> {
  const result = { ...values };
  for (const section of sections) {
    for (const field of section.fields) {
      if (!isActivityProgramToggleField(section, field)) continue;
      if (activityToggleValueUnset(result[field.field_key])) {
        result[field.field_key] = true;
      }
    }
  }
  return result;
}
